import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { 
  Clock, LogIn, LogOut, Coffee, UtensilsCrossed, 
  MapPin, MapPinOff, Loader2, AlertTriangle,
  ChevronDown, ChevronUp
} from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from '@/components/ui/dialog';

const PROXIMITY_CHECK_INTERVAL = 60000; // Check every 60 seconds when clocked in

const ClockWidget = () => {
  const { api, user } = useAuth();
  const { t } = useTranslation();
  const [clockStatus, setClockStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());
  // User-controlled toggle. Persisted in localStorage so it stays where
  // the user put it across page reloads. Auto-collapses by default when
  // the user is already clocked in (matches the previous behavior), but
  // any explicit toggle action wins.
  const [isMinimized, setIsMinimized] = useState(() => {
    if (typeof window === 'undefined') return false;
    const saved = window.localStorage.getItem('clock_widget_minimized');
    return saved === '1';
  });
  const [userToggled, setUserToggled] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.localStorage.getItem('clock_widget_minimized') !== null;
  });
  
  // Geofencing state
  const [locationPermission, setLocationPermission] = useState('unknown'); // 'unknown', 'granted', 'denied', 'unavailable'
  const [currentLocation, setCurrentLocation] = useState(null);
  const [proximityStatus, setProximityStatus] = useState(null); // { allowed, reason, location }
  const [showPermissionDialog, setShowPermissionDialog] = useState(false);
  const [checkingProximity, setCheckingProximity] = useState(false);
  
  const proximityCheckRef = useRef(null);
  const watchIdRef = useRef(null);

  const fetchStatus = useCallback(async () => {
    try {
      const response = await api.get('/hr/clock/status');
      setClockStatus(response.data);
    } catch (error) {
      console.error('Failed to fetch clock status:', error);
    } finally {
      setLoading(false);
    }
  }, [api]);

  // Check proximity with backend
  const checkProximity = useCallback(async (lat, lng) => {
    if (!lat || !lng) return null;
    
    setCheckingProximity(true);
    try {
      const response = await api.post('/hr/check-proximity', {
        latitude: lat,
        longitude: lng
      });
      setProximityStatus(response.data);
      return response.data;
    } catch (error) {
      console.error('Failed to check proximity:', error);
      return null;
    } finally {
      setCheckingProximity(false);
    }
  }, [api]);

  // Handle geolocation
  const getCurrentPosition = useCallback(() => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        setLocationPermission('unavailable');
        reject(new Error('Geolocation not supported'));
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          const coords = {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude
          };
          setCurrentLocation(coords);
          setLocationPermission('granted');
          resolve(coords);
        },
        (error) => {
          if (error.code === error.PERMISSION_DENIED) {
            setLocationPermission('denied');
          }
          reject(error);
        },
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 30000 }
      );
    });
  }, []);

  // Request location permission
  const requestLocationPermission = useCallback(async () => {
    try {
      const coords = await getCurrentPosition();
      await checkProximity(coords.latitude, coords.longitude);
      setShowPermissionDialog(false);
      return true;
    } catch (error) {
      if (error.code === 1) { // PERMISSION_DENIED
        toast.error('Location access denied. Please enable location in your browser settings.');
      } else {
        toast.error('Could not get your location. Please try again.');
      }
      return false;
    }
  }, [getCurrentPosition, checkProximity]);

  // Start watching location when clocked in
  const startLocationWatch = useCallback(() => {
    if (!navigator.geolocation || watchIdRef.current) return;

    watchIdRef.current = navigator.geolocation.watchPosition(
      async (position) => {
        const coords = {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude
        };
        setCurrentLocation(coords);
        
        // Check if still within proximity
        const status = await checkProximity(coords.latitude, coords.longitude);
        
        // Auto clock-out if moved away from work location
        if (status && !status.allowed && clockStatus?.status === 'clocked_in') {
          try {
            await api.post('/hr/clock/out', {
              latitude: coords.latitude,
              longitude: coords.longitude,
              auto_logout: true
            });
            toast.warning('You have been automatically clocked out as you left the work location area.');
            fetchStatus();
          } catch (error) {
            console.error('Auto clock-out failed:', error);
          }
        }
      },
      (error) => {
        console.error('Location watch error:', error);
      },
      { enableHighAccuracy: true, maximumAge: 30000 }
    );
  }, [api, checkProximity, clockStatus?.status, fetchStatus]);

  // Stop watching location
  const stopLocationWatch = useCallback(() => {
    if (watchIdRef.current) {
      navigator.geolocation.clearWatch(watchIdRef.current);
      watchIdRef.current = null;
    }
    if (proximityCheckRef.current) {
      clearInterval(proximityCheckRef.current);
      proximityCheckRef.current = null;
    }
  }, []);

  useEffect(() => {
    fetchStatus();
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    
    // Initial location check
    if (navigator.geolocation) {
      navigator.permissions?.query({ name: 'geolocation' }).then((result) => {
        setLocationPermission(result.state);
        if (result.state === 'granted') {
          getCurrentPosition().then(coords => {
            checkProximity(coords.latitude, coords.longitude);
          });
        }
      });
    } else {
      setLocationPermission('unavailable');
    }
    
    return () => {
      clearInterval(timer);
      stopLocationWatch();
    };
  }, [fetchStatus, getCurrentPosition, checkProximity, stopLocationWatch]);

  // Start/stop location watch based on clock status. Only auto-minimize
  // on first-clock-in if the user has not explicitly toggled the widget.
  useEffect(() => {
    if (clockStatus?.status === 'clocked_in') {
      startLocationWatch();
      if (!userToggled) setIsMinimized(true);
    } else {
      stopLocationWatch();
      if (!userToggled) setIsMinimized(false);
    }
  }, [clockStatus?.status, startLocationWatch, stopLocationWatch, userToggled]);

  const toggleMinimized = () => {
    setIsMinimized((prev) => {
      const next = !prev;
      try {
        window.localStorage.setItem('clock_widget_minimized', next ? '1' : '0');
      } catch { /* no-op */ }
      setUserToggled(true);
      return next;
    });
  };

  const handleClockIn = async () => {
    // Check if location permission is needed
    if (locationPermission === 'unknown' || locationPermission === 'prompt') {
      setShowPermissionDialog(true);
      return;
    }
    
    if (locationPermission === 'denied') {
      toast.error('Location access is required to clock in. Please enable location in your browser settings.');
      return;
    }

    setActionLoading(true);
    try {
      let coords = currentLocation;
      
      // Get fresh location
      if (!coords) {
        coords = await getCurrentPosition();
      }
      
      // Verify proximity before clocking in
      const proximity = await checkProximity(coords.latitude, coords.longitude);
      
      if (proximity && !proximity.allowed) {
        toast.error(proximity.reason || 'You must be at a work location to clock in');
        setActionLoading(false);
        return;
      }
      
      await api.post('/hr/clock/in', {
        latitude: coords.latitude,
        longitude: coords.longitude
      });
      
      toast.success(`Clocked in at ${proximity?.location || 'work location'}`);
      fetchStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to clock in');
    } finally {
      setActionLoading(false);
    }
  };

  const handleClockOut = async () => {
    setActionLoading(true);
    try {
      let coords = currentLocation;
      
      try {
        coords = await getCurrentPosition();
      } catch (e) {
        // Continue without location
      }
      
      const response = await api.post('/hr/clock/out', {
        latitude: coords?.latitude,
        longitude: coords?.longitude,
        auto_logout: false
      });
      
      toast.success(`Clocked out - Total: ${response.data.total_hours} hours`);
      fetchStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to clock out');
    } finally {
      setActionLoading(false);
    }
  };

  const handleLunchOut = async () => {
    setActionLoading(true);
    try {
      await api.post('/hr/clock/lunch-out');
      toast.success('Lunch break started');
      fetchStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start lunch');
    } finally {
      setActionLoading(false);
    }
  };

  const handleLunchIn = async () => {
    setActionLoading(true);
    try {
      await api.post('/hr/clock/lunch-in');
      toast.success('Returned from lunch');
      fetchStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to end lunch');
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return null;
  }

  // Don't show if time tracking is disabled
  if (!clockStatus?.track_time) {
    return null;
  }

  const status = clockStatus.status;
  const entry = clockStatus.entry;
  const canClockIn = proximityStatus?.allowed !== false || locationPermission === 'unavailable';
  
  // User-controlled minimized vs expanded — no hover behavior. Clicking
  // the minimized tab expands; clicking the chevron in the header
  // collapses.
  const showMinimized = isMinimized;

  return (
    <>
      <div className="relative">
        {/* Minimized Tab — click anywhere to expand */}
        {showMinimized && (
          <button
            type="button"
            onClick={toggleMinimized}
            className={`${
              status === 'clocked_in' ? 'bg-green-600 hover:bg-green-500' :
              status === 'on_lunch' ? 'bg-orange-600 hover:bg-orange-500' :
              'bg-slate-700 hover:bg-slate-600'
            } rounded-lg px-3 py-2 cursor-pointer shadow-lg flex items-center gap-2 transition-all duration-200`}
            data-testid="clock-widget-minimized"
            title="Click to expand Time Clock"
          >
            <Clock className="h-4 w-4 text-white" />
            <span className="text-white text-sm font-medium">
              {status === 'clocked_in' ? 'Working' :
                status === 'on_lunch' ? 'On Lunch' :
                'Time Clock'}
            </span>
            <span className="text-white/80 text-xs font-mono">
              {currentTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </span>
            <ChevronUp className="h-3 w-3 text-white/80 ml-1" />
          </button>
        )}

        {/* Full Widget — explicit toggle (no hover) */}
        {!showMinimized && (
          <Card className="bg-slate-800 border-slate-700 shadow-xl transition-all duration-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Clock className="h-5 w-5 text-blue-400" />
                  <span className="text-white font-medium">{t('clock.title', 'Time Clock')}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-2xl font-mono text-green-400">
                    {currentTime.toLocaleTimeString()}
                  </span>
                  <button
                    type="button"
                    onClick={toggleMinimized}
                    className="text-slate-400 hover:text-white hover:bg-slate-700 rounded p-1 transition-colors"
                    title="Minimize"
                    data-testid="clock-widget-minimize-btn"
                  >
                    <ChevronDown className="h-4 w-4" />
                  </button>
                </div>
              </div>

              {/* Location Status */}
              <div className="flex items-center gap-2 mb-3 text-xs">
                {checkingProximity ? (
                  <Badge variant="outline" className="border-slate-600 text-slate-400">
                    <Loader2 className="h-3 w-3 animate-spin mr-1" />
                    {t('clock.checkingLocation', 'Checking location...')}
                  </Badge>
                ) : proximityStatus?.allowed ? (
                  <Badge variant="outline" className="border-green-600 text-green-400">
                    <MapPin className="h-3 w-3 mr-1" />
                    {proximityStatus.location || t('clock.atWorkLocation', 'At work location')}
                  </Badge>
                ) : proximityStatus?.allowed === false ? (
                  <Badge variant="outline" className="border-red-600 text-red-400">
                    <MapPinOff className="h-3 w-3 mr-1" />
                    {t('clock.notAtWork', 'Not at work location')}
                  </Badge>
                ) : locationPermission === 'denied' ? (
                  <Badge variant="outline" className="border-orange-600 text-orange-400">
                    <AlertTriangle className="h-3 w-3 mr-1" />
                    {t('clock.locationDenied', 'Location access denied')}
                  </Badge>
                ) : null}
              </div>

              <div className="flex items-center gap-2 mb-3">
                <Badge className={
                  status === 'clocked_in' ? 'bg-green-600' :
                  status === 'on_lunch' ? 'bg-orange-600' :
                  status === 'completed' ? 'bg-blue-600' :
                  'bg-slate-600'
                }>
                  {status === 'clocked_in' ? t('clock.working', 'Working') :
                   status === 'on_lunch' ? t('clock.onLunch', 'On Lunch') :
                   status === 'completed' ? t('clock.dayComplete', 'Day Complete') :
                   t('clock.notClockedIn', 'Not Clocked In')}
                </Badge>
                {entry?.clock_in && (
                  <span className="text-slate-400 text-sm">
                    {t('clock.in', 'In')}: {entry.clock_in}
                    {entry.clock_out && ` | ${t('clock.out', 'Out')}: ${entry.clock_out}`}
                    {entry.auto_logout && <span className="text-orange-400"> ({t('clock.auto', 'auto')})</span>}
                  </span>
                )}
              </div>

              <div className="flex gap-2">
                {status === 'not_clocked_in' && (
                  <Button 
                    className={`flex-1 ${canClockIn ? 'bg-green-600 hover:bg-green-500' : 'bg-slate-600 cursor-not-allowed'}`}
                    onClick={handleClockIn}
                    disabled={actionLoading || (!canClockIn && locationPermission !== 'unknown')}
                    data-testid="clock-in-btn"
                  >
                    {actionLoading ? (
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    ) : (
                      <LogIn className="h-4 w-4 mr-2" />
                    )}
                    {t('clock.clockIn', 'Clock In')}
                  </Button>
                )}

                {status === 'clocked_in' && (
                  <>
                    <Button 
                      className="flex-1 bg-orange-600 hover:bg-orange-500"
                      onClick={handleLunchOut}
                      disabled={actionLoading}
                      data-testid="lunch-out-btn"
                    >
                      <Coffee className="h-4 w-4 mr-2" />
                      {t('clock.lunchBreak', 'Lunch Break')}
                    </Button>
                    <Button 
                      className="flex-1 bg-red-600 hover:bg-red-500"
                      onClick={handleClockOut}
                      disabled={actionLoading}
                      data-testid="clock-out-btn"
                    >
                      {actionLoading ? (
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      ) : (
                        <LogOut className="h-4 w-4 mr-2" />
                      )}
                      {t('clock.clockOut', 'Clock Out')}
                    </Button>
                  </>
                )}

                {status === 'on_lunch' && (
                  <Button 
                    className="flex-1 bg-green-600 hover:bg-green-500"
                    onClick={handleLunchIn}
                    disabled={actionLoading}
                    data-testid="lunch-in-btn"
                  >
                    <UtensilsCrossed className="h-4 w-4 mr-2" />
                    {t('clock.endLunch', 'End Lunch')}
                  </Button>
                )}

                {status === 'completed' && entry && (
                  <div className="flex-1 text-center">
                    <p className="text-slate-400 text-sm">{t('clock.todaysTotal', 'Today\'s Total')}</p>
                    <p className="text-2xl font-bold text-green-400">{entry.total_hours} {t('clock.hours', 'hours')}</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Location Permission Dialog */}
      <Dialog open={showPermissionDialog} onOpenChange={setShowPermissionDialog}>
        <DialogContent className="bg-slate-900 border-slate-800">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <MapPin className="h-5 w-5 text-blue-400" />
              Location Access Required
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              To ensure accurate time tracking, we need to verify you are at a designated work location before clocking in.
              <br /><br />
              Your location will only be used for clock-in verification and is not stored or shared.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setShowPermissionDialog(false)}
              className="border-slate-700"
            >
              Cancel
            </Button>
            <Button 
              onClick={requestLocationPermission}
              className="bg-blue-600 hover:bg-blue-500"
            >
              <MapPin className="h-4 w-4 mr-2" />
              Allow Location Access
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ClockWidget;
